import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Scale,
  Search,
  ChevronsDownUp,
  ChevronsUpDown,
  FolderTree,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { lookupPasalRefs, type PasalData } from "@/data/uuPnbpPasal";
import PublicHeader from "@/components/common/PublicHeader";
import { PNBP_TREE, type TreeSubItem } from "@/data/pnbpTree";
import { CategoryCard, PasalModal } from "@/components/public/StrukturPnbpTree";


// ─── Page ─────────────────────────────────────────────────────────────────────

const PRIMARY = "#1B5189";

export default function PublicStrukturPage() {
  const [query, setQuery] = useState("");
  const [forceOpen, setForceOpen] = useState<boolean | null>(null);
  const [modal, setModal] = useState<{ item: TreeSubItem; pasals: PasalData[] } | null>(null);

  const handlePasalClick = useCallback((item: TreeSubItem, pasals: PasalData[]) => {
    setModal({ item, pasals });
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return PNBP_TREE;
    const q = query.toLowerCase();
    return PNBP_TREE.filter((cat) => {
      if (cat.title.toLowerCase().includes(q)) return true;
      return cat.items.some(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q) ||
          item.pasal?.some((p) => p.toLowerCase().includes(q)) ||
          item.children?.some(
            (c) => c.name.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q),
          ),
      );
    });
  }, [query]);

  const totalSubItems = PNBP_TREE.reduce((acc, cat) => acc + cat.items.length, 0);
  const totalLeaves = PNBP_TREE.reduce(
    (acc, cat) => acc + cat.items.reduce((a, item) => a + (item.children?.length ?? 0), 0),
    0,
  );
  const totalPasalWithContent = useMemo(() => {
    const keys = new Set<string>();
    PNBP_TREE.forEach((cat) =>
      cat.items.forEach((item) => {
        if (item.pasal) lookupPasalRefs(item.pasal).forEach((p) => keys.add(p.key));
      }),
    );
    return keys.size;
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <PublicHeader />

      {modal && (
        <PasalModal pasals={modal.pasals} subItemName={modal.item.name} onClose={() => setModal(null)} />
      )}

      {/* ─── Page hero ──────────────────────────────────────────── */}
      <section className="bg-slate-50 border-b border-slate-200 py-12">
        <div className="mx-auto max-w-5xl px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1 mb-6 text-xs text-slate-400">
            <Link to="/" className="hover:text-slate-600 transition-colors">
              Beranda
            </Link>
            <ChevronRight className="h-3 w-3 flex-shrink-0" />
            <span className="text-slate-700 font-semibold">Struktur PNBP</span>
          </nav>

          <div className="flex items-start gap-4">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-xl text-white flex-shrink-0"
              style={{ backgroundColor: PRIMARY }}
            >
              <FolderTree className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Struktur Pengetahuan PNBP</h1>
              <p className="text-sm text-gray-500 mt-1">
                Kerangka pengetahuan PNBP berdasarkan UU No. 9 Tahun 2018 · klik{" "}
                <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-xs font-medium border border-blue-200">
                  <BookOpen className="h-3 w-3" />
                  Baca
                </span>{" "}
                untuk membaca teks resmi dari UU
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Content ────────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 py-10 space-y-6">
        {/* Source badge */}
        <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <Scale className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-blue-900">Sumber Hukum</p>
            <p className="text-blue-700 mt-0.5">
              <strong>UU No. 9 Tahun 2018</strong> tentang Penerimaan Negara Bukan
              Pajak — disahkan 23 Agustus 2018, LN Tahun 2018 No. 147, TLN No.
              6245. Menggantikan UU No. 20 Tahun 1997.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Kategori Utama", value: PNBP_TREE.length },
            { label: "Sub-Topik", value: totalSubItems },
            { label: "Poin Detail", value: totalLeaves },
            { label: "Pasal Tersedia", value: totalPasalWithContent },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 px-3 py-3 text-center">
              <p className="text-xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-tight">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* ── HIDDEN: Struktur list/feature disabled for now (search + tree + disclaimer). Restore by flipping false → true. ── */}
        {false && (
        <>
        {/* Search + controls */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cari kategori, sub-topik, atau nomor pasal..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setForceOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            <ChevronsUpDown className="h-4 w-4" />
            <span className="hidden sm:inline">Buka Semua</span>
          </button>
          <button
            type="button"
            onClick={() => setForceOpen(false)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            <ChevronsDownUp className="h-4 w-4" />
            <span className="hidden sm:inline">Tutup Semua</span>
          </button>
        </div>

        {/* Tree */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <Search className="h-8 w-8 mb-2 text-gray-300" />
            <p className="text-sm">Tidak ada hasil untuk &ldquo;{query}&rdquo;</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((cat) => (
              <CategoryCard key={cat.id} category={cat} forceOpen={forceOpen} onPasalClick={handlePasalClick} />
            ))}
          </div>
        )}

        <p className="text-xs text-gray-400 text-center pb-4">
          Teks pasal bersumber dari salinan resmi UU No. 9 Tahun 2018. Untuk
          verifikasi selalu merujuk ke dokumen hukum resmi di JDIH Kemenkeu.
        </p>
        </>
        )}
      </section>

      {/* ─── Footer ─────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="flex flex-col items-center justify-between gap-4 text-xs text-slate-500 md:flex-row">
            <div>© 2026 Direktorat Jenderal Anggaran. All rights reserved.</div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> dja@kemenkeu.go.id</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Jakarta, Indonesia</span>
              <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> +62-xxxxxxxxxx</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
